import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import LinkWrapper from "@/components/linkWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import BackgroundImageWrapper from "@/components/backgroundImageWrapper";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/typography'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment ContentWithTestimonialAndStatsData on StrapiComponentContentWithTestimonialAndStats {
    title
    topic
    body {
      ...StrapiSharedBody
    }
    stats {
      label
      value
    }
    button {
      ...StrapiSharedLink
    }
    testimonial {
      name
      jobTitle
      text
      companyLogo {
        ...StrapiSharedLink
      }
      image {
        ...StrapiMediaSubquery
      }
    }
  }
`;

function WithTestimonialAndStats({ data }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="relative bg-background-white py-16 sm:py-24">
      <div className="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-2 lg:items-start lg:gap-24 lg:px-8">
        <div className="relative sm:py-16 lg:py-0">
          <div
            aria-hidden="true"
            className="hidden sm:block lg:absolute lg:inset-y-0 lg:right-0 lg:w-screen"
          >
            <div className="absolute inset-y-0 right-1/2 w-full rounded-r-3xl bg-background-off-white lg:right-72" />
            <svg
              className="absolute left-1/2 top-8 -ml-3 lg:-right-8 lg:left-auto lg:top-12"
              width={404}
              height={392}
              fill="none"
              viewBox="0 0 404 392"
            >
              <defs>
                <pattern
                  id="02f20b47-fd69-4224-a62a-4c9de5c763f7"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-text-light-gray"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={392}
                fill="url(#02f20b47-fd69-4224-a62a-4c9de5c763f7)"
              />
            </svg>
          </div>

          <div className="relative mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:max-w-none lg:px-0 lg:py-20">
            {data.testimonial && (
              <BackgroundImageWrapper
                image={data.testimonial.image}
                className="relative h-full w-full overflow-hidden rounded-2xl object-cover pb-10 pt-64 shadow-xl"
              >
                <div className="absolute inset-0 bg-background-primary mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-background-primary via-background-primary opacity-90" />
                <div className="relative px-8">
                  {(data.testimonial.companyLogo.overwriteIcon ||
                    data.testimonial.companyLogo.link?.icon) && (
                    <div>
                      <LinkWrapper
                        to={data.testimonial?.companyLogo?.link?.url}
                        className="inline-flex"
                      >
                        <MediaWrapper
                          media={
                            data.testimonial.companyLogo.overwriteIcon ||
                            data.testimonial.companyLogo.link.icon
                          }
                          divClassName="h-12"
                          objectFit="contain"
                        />
                      </LinkWrapper>
                    </div>
                  )}
                  <blockquote className="mt-8">
                    <div className="relative text-lg font-medium text-text-white md:flex-grow">
                      <svg
                        className="absolute left-0 top-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-text-primary"
                        fill="currentColor"
                        viewBox="0 0 32 32"
                        aria-hidden="true"
                      >
                        <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                      </svg>
                      <MarkdownWrapper
                        text={data.testimonial.text}
                        markdownClassName="relative"
                        replaceParagraph={false}
                      />
                    </div>

                    <footer className="mt-4">
                      {data.testimonial.name && (
                        <MarkdownWrapper
                          text={`${data.testimonial.name}${
                            data.testimonial.jobTitle
                              ? `, ${data.testimonial.jobTitle}`
                              : ""
                          }`}
                          markdownClassName="text-base font-semibold text-text-primary-light"
                        />
                      )}
                    </footer>
                  </blockquote>
                </div>
              </BackgroundImageWrapper>
            )}
          </div>
        </div>

        <div className="relative mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-0">
          <div className="pt-12 sm:pt-16 lg:pt-20">
            {data.topic && (
              <h3 className="block text-base font-semibold uppercase tracking-wide text-text-secondary">
                <MarkdownWrapper text={data.topic} />
              </h3>
            )}
            {data.title && (
              <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
                <MarkdownWrapper text={data.title} />
              </h2>
            )}
            {data.body && data.body.text && (
              <div className="mt-6 space-y-6 text-text-secondary">
                <MarkdownWrapper
                  text={data.body.text}
                  images={data.body.images}
                  imgDivClassName="w-full"
                  imgClassName="rounded-lg"
                  width={1310}
                  height={873}
                  replaceParagraph={false}
                />
              </div>
            )}
          </div>

          {data.stats && (
            <div className="mt-10">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-8">
                {data.stats.map((stat, index) => (
                  <div
                    key={index}
                    className="border-t-2 border-border-light-gray pt-6"
                  >
                    <dt className="text-base font-medium text-text-secondary">
                      {stat.label}
                    </dt>
                    <dd className="text-3xl font-extrabold tracking-tight text-text-primary">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
              {data.button?.link?.url &&
                (data.button?.overwriteTitle || data.button?.link?.title) && (
                  <div className="mt-10">
                    <LinkWrapper
                      to={data.button.link.url}
                      className="text-base font-medium text-text-primary"
                    >
                      {" "}
                      {data.button.overwriteTitle || data.button.link.title}
                      <span aria-hidden="true">&rarr;</span>{" "}
                    </LinkWrapper>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

WithTestimonialAndStats.propTypes = {
  data: PropTypes.object,
};

export default WithTestimonialAndStats;
